import { KalturaPurchaseStatus } from '@kaltura-ott/ott-client-types/api/types';
import { KalturaUserAssetRule } from '@kaltura-ott/ott-client-types/api/types/KalturaUserAssetRule';
import { KuxFavorite, KuxLinearType, KuxProgramType } from '@kaltura-ott/tvpil';
import { observable, action, computed } from 'mobx';
import { container } from 'tsyringe';

import { createNewChannelsPclDictionary } from '../../rhino/data/channel/personalization/utils';
import { getChannelAssetsQueryData } from '../../rhino/data/channel/queryData';
import { getPersonalLineup } from '../../rhino/data/personalLineup';
import { fetchChannelAssetRulesQuery } from '../../rhino/data/rules/fetchChannelAssetRulesQuery';
import { errorHandling } from '../../utils/error-handling';
import { DmsStore } from '../dms';
import type { FavoriteRepository } from '../favoriteRepository';

import { addCurrentProgramToLinear } from './entities.service';
import { PlayableAsset } from './PlayableAsset';
import { Program } from './Program';
import type { ProgramPlaceholder } from './ProgramPlaceholder';

export class Linear extends PlayableAsset<KuxLinearType> {
  static moduleName = 'Linear';

  @observable public currentProgram?: Program;
  @observable public programs: (Program | ProgramPlaceholder)[] = [];
  @observable watchedCount = 0;
  @observable linearEntitlement?: boolean;

  @computed get isPurchased(): boolean | undefined {
    // TODO refactor super.isPurchased
    if (this.price) {
      return ![
        KalturaPurchaseStatus.forPurchase,
        KalturaPurchaseStatus.forPurchaseSubscriptionOnly,
        KalturaPurchaseStatus.pendingEntitlement,
      ].includes(this.price.purchaseStatus);
    }
    return this.linearEntitlement;
  }

  get linearId(): number {
    return this.id;
  }

  async setupRules() {
    this.rules = (await fetchChannelAssetRulesQuery(this.data.id)) as unknown as KalturaUserAssetRule[];
  }

  @computed get isPlayable() {
    if (!this.rules) {
      return false;
    }
    if (!this.isPurchased) {
      return false;
    }
    return true;
  }

  get mediaFileId(): number | undefined {
    return this.data.mediaFileId;
  }

  @action.bound
  setupCurrentProgram(programData: KuxProgramType) {
    if (this.currentProgram?.id !== programData.id) {
      this.currentProgram = new Program(programData);
      this.currentProgram.init(programData);
    }
  }

  public get favoriteInstance(): KuxFavorite | undefined {
    const favoriteRepository = container.resolve<FavoriteRepository>('favoriteRepository');
    return favoriteRepository.getFavorite(this.data.id);
  }

  @computed public get isFavorite(): boolean {
    const favoriteRepository = container.resolve<FavoriteRepository>('favoriteRepository');
    return Boolean(favoriteRepository.favorites[this.data.id]);
  }

  /**
   * Сhanges the status of favorites
   * @param state
   */
  @action
  async setFavorite(state: boolean): Promise<boolean> {
    const favoriteRepository = container.resolve<FavoriteRepository>('favoriteRepository');
    const status = await favoriteRepository.setFavorite(state, this.data.id);
    return status;
  }

  @computed public get parentalRating(): number {
    return this.data.parentalRating;
  }

  public get channelNumber(): number {
    const personalLineupData = getPersonalLineup();

    const channelAssetsData = getChannelAssetsQueryData();

    if (!personalLineupData) {
      return this.data.channelNumber;
    }

    // should not happen
    if (!channelAssetsData) {
      return this.data.channelNumber;
    }

    const { dictionary, highestChannelNumber } = personalLineupData;

    return (
      dictionary.get(this.data.id) ??
      createNewChannelsPclDictionary(channelAssetsData.channelMap, dictionary, highestChannelNumber).get(this.data.id)
    );
  }

  @errorHandling()
  public async fetchCurrentProgram(): Promise<void> {
    const dmsStore = container.resolve<DmsStore>('dmsStore');
    await addCurrentProgramToLinear([this], undefined, true, dmsStore.dms.mosaicInfo.isMosaicSupported);
  }
  get isHiddenLinear() {
    return this.data.linearChannelType === 'hidden';
  }
  get isMosaicLinear() {
    return this.data.linearChannelType === 'mosaic';
  }
}
